import { NavLink } from 'react-router-dom';
import ProptPrice from '../ProptPrice';
import SidebarSteps from '../SidebarSteps';
import { useEffect, useState } from 'react';
import DepositModal from '../DepositModal';
import { useProptPrice } from '../../hooks/useProptPrice';


const DashboardNotValidated = ({ balances, kycCompleted }) => {
   const proptPrice = useProptPrice();

   useEffect(() => {
      localStorage.removeItem('mnemonicPhrase')
      fetchBalances();
   }, [])
   const disableUSDFeatures = true;
   const kycAlertMessage = 'KYC approval is required to use USD-related features.';

   const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
   const [initialStep, setInitialStep] = useState(1);
   const [selectedCrypto, setSelectedCrypto] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [walletData, setWalletData] = useState([]);
   const [totalBalance, setTotalBalance] = useState(0);

   // para precio propt estable
   const [rates, setRates] = useState(null);

   const cryptoInfo = {
      USD: { name: 'US Dollar', image: '/assets/img/icon_dollar.svg' },
      USDC: { name: 'USDC', image: '/assets/img/icon_usdc.svg' },
      USDT: { name: 'USDT', image: '/assets/img/icon_usdt.svg' },
      BTC: { name: 'BTC Bitcoin', image: '/assets/img/icon_btc.svg' },
      ETH: { name: 'ETH Ethereum', image: '/assets/img/icon_eth.svg' },
      POL: { name: 'POL Polygon', image: '/assets/img/icon_pol.svg' },
      PROPT: { name: 'PROPT ProperT', image: '/assets/img/icon_propt_n.svg' },
      'PROPT-ETHEREUM': { name: 'PROPT-ETHEREUM', fullName: 'ProperT (Ethereum)', image: '/assets/img/icon_propt_n.svg' },
      'PROPT-POLYGON': { name: 'PROPT-POLYGON', fullName: 'ProperT (Polygon)', image: '/assets/img/icon_propt_n.svg' },
   };

   const supportedCryptoList = [
      'BTC',
      'ETH',
      'POL',
      'PROPT-ETHEREUM',
      'PROPT-POLYGON',
   ];

   const handleDepositUSDClick = () => {
      setInitialStep(2);
      setIsDepositModalOpen(true);
   };

   // Para precio propt estable
   useEffect(() => {
      // La lógica de la API ahora vive aquí adentro
      const fetchRates = async () => {
         setIsLoading(true);
         try {
            const url = 'https://pt-api.digit-a.net/api/conversions';
            const response = await fetch(url);

            if (!response.ok) {
               throw new Error(`API Error: ${response.status}`);
            }

            const data = await response.json();
            setRates(data);
         } catch (error) {
            console.error("Could not load token prices.", error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchRates();
   }, []);

   const getPrice = (tokenName, currencySymbol = '$', decimals = 2) => {
      if (isLoading) return 'Loading...';
      if (!rates || typeof rates[tokenName] === 'undefined') {
         return 'Not Available';
      }
      if (tokenName === 'PROPT') {
         return `${currencySymbol}${rates[tokenName].toFixed(2)}`;
      }
      return `${currencySymbol}${rates[tokenName].toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
   };


   const handleCryptoDeposit = async (cryptoName, imagePath, currencyCode) => {
      try {
         setIsLoading(true);
         const jwt = localStorage.getItem('jwt');
         if (!jwt) throw new Error('No authentication token found');

         const response = await fetch(`https://pt-api.digit-a.net/api/deposit/deposit-coin/select-currency/${currencyCode}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' }
         });
         if (!response.ok) throw new Error(`API Error: ${response.status}`);

         const data = await response.json();
         setSelectedCrypto({
            name: cryptoName,
            image: imagePath,
            qrCode: data.qrCode,
            address: data.address,
            currency: data.currency
         });
         setInitialStep(4);
         setIsDepositModalOpen(true);
      } catch (error) {
         console.error('Error fetching deposit information:', error);
         // Manejar el error visualmente si es necesario
      } finally {
         setIsLoading(false);
      }
   };

   const fetchBalances = async () => {
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) return;

         const response = await fetch('https://pt-api.digit-a.net/api/wallets/get-general-balance', {
            method: 'GET',
            headers: {
               'Authorization': `Bearer ${jwt}`,
               'Content-Type': 'application/json'
            }
         });
         if (!response.ok) throw new Error('Failed to fetch balances');

         const data = await response.json();

         // Lógica para añadir wallets vacías si no existen
         const currenciesToShow = ['USD', 'USDC', 'USDT', 'BTC', 'ETH', 'POL', 'PROPT'];
         const existingCurrencies = new Set((data.wallets || []).map(w => w.currency));
         const allWallets = [...(data.wallets || [])];

         currenciesToShow.forEach(currency => {
            if (!existingCurrencies.has(currency)) {
               allWallets.push({ currency: currency, amount: 0, rate: 0 });
            }
         });

         setWalletData(allWallets);
         setTotalBalance(data.balance || 0);
      } catch (error) {
         console.error('Error fetching balances:', error);
      }
   };


   const renderCryptoBox = (currency, data) => {
      const crypto = cryptoInfo[currency];
      const amount = data?.amount || 0;
      const isButtonDisabled = disableUSDFeatures && ['USDC', 'USDT'].includes(currency);

      let rate;
      if (currency.startsWith('PROPT')) {
         rate = proptPrice;
      } else {
         rate = data?.rate || 0;
      }

      return (
         <div className="ppt_box_gen">
            <div className="d-flex align-items-start gap-4 ppt_cryptoinfo">
               <img src={process.env.PUBLIC_URL + crypto.image} alt={crypto.name} />
               <div className="ppt_cryptoinfo_title">
                  <span>{crypto.name}</span>
                  <div className='d-flex gap-lg-3 flex-column flex-lg-row'>

                     <small>Last Price <em>${
                        typeof rate === 'number'
                           ? currency.startsWith('PROPT')
                              ? getPrice('PROPT', '')
                              : (rate > 10
                                 ? rate.toLocaleString('en-US')
                                 : rate.toFixed(rate < 1 ? 4 : 2))
                           : '0.00'
                     }</em></small>

                     <small>Balance <em>{parseFloat(amount).toFixed(4)}</em></small>
                  </div>
               </div>
               <NavLink
                  to="."
                  onClick={(e) => {
                     e.preventDefault();
                     if (isButtonDisabled) {
                        alert(kycAlertMessage);
                     } else {
                        let currencyCodeForApi = currency; // Por defecto, es el mismo nombre

                        if (currency === 'PROPT-ETHEREUM') {
                           currencyCodeForApi = 'PROPT-ETH';
                        } else if (currency === 'PROPT-POLYGON') {
                           currencyCodeForApi = 'PROPT-POL';
                        }

                        handleCryptoDeposit(crypto.fullName, crypto.image, currencyCodeForApi);
                     }
                  }}
                  className="ppt_btn_primary ms-auto alt px-lg-5"
               >
                  Deposit
               </NavLink>


            </div>
         </div>
      );
   };

   return (
      <>
         <div className="row mb-5">
            <div className="col col-lg-3 mb-4 mb-lg-0">
               <div className="ppt_header d-flex justify-content-between align-items-center">
                  <nav className="ppt_navdash_scd">
                     <NavLink to="/dashboard" className="link active">Balances</NavLink>
                     <NavLink to="/activity" className="link">Activity</NavLink>
                  </nav>
               </div>
            </div>
            <div className="col d-flex gap-5 align-items-start">
               <SidebarSteps activeStep={4} kycCompleted={kycCompleted} />
               <a
                  className="ppt_btn_primary kyc_start"
                  href="/kyc"
               // style={{ padding: '13px 18px', fontSize: '14px' }}
               >
                  {kycCompleted ? 'Review KYC' : 'To buy start KYC'}
               </a>
            </div>
         </div>
         <div className="row mb-5 mx-auto max">
            <div className="col px-0 px-lg-3">
               <h3 className="ppt_form_title mb-3">Balances</h3>
               <div className="ppt_box_gen">
                  <div className="d-flex align-items-center gap-4 ppt_cryptoinfo">
                     <img src={process.env.PUBLIC_URL + '/assets/img/icon_dollar.svg'} data-lightmodeimg alt="" />
                     <div className="ppt_cryptoinfo_title">
                        <span>US Dollar</span>
                        <em>${totalBalance.toFixed(2)}</em>
                     </div>
                     <div className="ppt_cryptoinfo_name">
                        <small>$1 USD = {getPrice("PROPT")} PROPT = $1 USD</small>
                        <small>Use them interchangeably on the ProperT platform and withdraw funds anytime as any of these assets.</small>
                     </div>
                     <NavLink
                        to="."
                        onClick={(e) => {
                           e.preventDefault();
                           if (disableUSDFeatures) {
                              alert(kycAlertMessage);
                           } else {
                              handleDepositUSDClick();
                           }
                        }}
                        className="ppt_btn_primary ms-auto alt px-lg-5"
                     >
                        Deposit USD
                     </NavLink>

                  </div>
               </div>
            </div>
         </div>

         <div className="row mb-5 mx-auto max">
            <div className="col-12 px-0 px-lg-3">
               <h3 className="ppt_form_title mb-3">Stablecoins</h3>
            </div>
            {['USDC', 'USDT'].map(currency => {
               const wallet = walletData.find(w => w.currency === currency) || { currency, amount: 0 };
               return (
                  <div className="col col-lg-6 mb-4 px-0 px-lg-3" key={currency}>
                     {renderCryptoBox(currency, wallet)}
                  </div>
               );
            })}
         </div>
         <div className="row mb-5 mx-auto max">
            <div className="col-12 px-0 px-lg-3">
               <h3 className="ppt_form_title mb-3">Supported Crypto</h3>
            </div>

            {supportedCryptoList.map((currencyName) => {
               const dataKey = currencyName.startsWith('PROPT') ? 'PROPT' : currencyName;
               const wallet = walletData.find(w => w.currency === dataKey) || { currency: dataKey, amount: 0 };
               return (
                  <div className="col-12 col-lg-6 mb-4 px-0 px-lg-3" key={currencyName}>
                     {renderCryptoBox(currencyName, wallet)}
                  </div>
               );
            })}
         </div>




         <DepositModal
            isOpen={isDepositModalOpen}
            onClose={() => {
               setIsDepositModalOpen(false);
               setInitialStep(1);
               setSelectedCrypto(null);
            }}
            initialStep={initialStep}
            initialCrypto={selectedCrypto}
         />
      </>
   );
};
export default DashboardNotValidated;